import { defaultRegistry } from 'react-sweet-state';

import type { BadStatusError } from '@confluence/network';
import { containsNetworkError } from '@confluence/network';
import {
	apolloCompatibleQuery,
	type ApolloCompatibleQueryResult,
} from '@confluence/apollo-relay-interop/entry-points/apolloCompatibleQuery.ts';

import type { FetchOptions } from './fetchResponse.ts';
import { fetchResponse } from './fetchResponse.ts';
import { queryStateStore } from './useExperimentalQueryState.ts';
import { type TransformErrorType } from './handleError.ts';
import { generateCacheKey } from './generateCacheKey.ts';
import { convertToApolloError } from './convertToApolloError.ts';

export type FetchPolicy = 'cache-first' | 'network-only' | 'cache-and-network' | 'no-cache';
export type ErrorPolicy = 'none' | 'all';

type GetExperimentalQueryParams<TData, TVariables, TResponse> = FetchOptions<
	TVariables,
	TResponse
> & {
	variables: TVariables;
	parseResult: (response: TResponse, variables: TVariables) => TData | Promise<TData>;
	fetchPolicy?: FetchPolicy;
	errorPolicy?: ErrorPolicy;
	skip?: boolean;
	onError?: (error: Error | BadStatusError) => void;
	ignoreNoNetworkError?: boolean;
	transformError?: TransformErrorType;
};

export async function experimentalQuery<TData, TVariables, TResponse>({
	url,
	method = 'GET',
	headers,
	body,
	credentials,
	variables,
	parseResult,
	fetchPolicy = 'cache-first',
	errorPolicy = 'none',
	skip = false,
	onError,
	parseResponseOnSuccess,
	parseResponseOnError,
	ignoreNoNetworkError,
	transformError,
}: GetExperimentalQueryParams<TData, TVariables, TResponse>): Promise<
	ApolloCompatibleQueryResult<TData>
> {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		return { data: undefined as TData, errors: [] };
	}

	const key = generateCacheKey({
		url,
		method,
		headers,
		body,
		credentials,
		variables,
	});

	const fetchData = async () => {
		try {
			const json = await fetchResponse<TVariables, TResponse>(
				{
					url,
					method,
					body,
					headers,
					credentials,
					parseResponseOnSuccess,
					parseResponseOnError,
				},
				variables,
			);

			return await parseResult(json, variables);
		} catch (error) {
			if (ignoreNoNetworkError && containsNetworkError(error)) {
				return undefined as TData;
			}

			throw error;
		}
	};

	const storeInstance = defaultRegistry.getStore(queryStateStore);

	const getCachedData = (): TData | undefined => {
		const queryState = storeInstance.storeState.getState() as Record<string, any>;
		return queryState[key]?.data;
	};

	const setCachedData = (data: TData) => {
		storeInstance.actions.setQueryState(key, {
			data,
			loading: false,
			error: undefined,
			called: true,
		});
	};

	const handleConvertToApolloError = async (error: Error | BadStatusError) => {
		return convertToApolloError((await transformError?.(error)) || error);
	};

	return apolloCompatibleQuery<TData>({
		fetchData,
		fetchPolicy,
		errorPolicy,
		skip,
		getCachedData,
		setCachedData,
		onError,
		convertToApolloError: handleConvertToApolloError,
	});
}
